import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import api from '../../config/axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/common/Toast';
import { 
  BookOpen, 
  Clock, 
  User, 
  ChevronLeft,
  Eye,
  FileText,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Info,
  MonitorPlay,
  Lock,
  MessageCircle,
  Sparkles,
  Download,
  Star
} from 'lucide-react';

// Components
import ErrorBoundary from '../../components/common/ErrorBoundary';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import VideoPlayer from '../../components/lesson/VideoPlayer';
import QuestionSection from '../../components/lesson/QuestionSection';
import LessonSidebar from '../../components/lesson/LessonSidebar';
import InlinePDFViewer from '../../components/lesson/InlinePDFViewer';

// Hooks
import { useVideoPlayer } from '../../hooks/useVideoPlayer';
import {
  useLessonData,
  useLessonQuestions,
  useLessonAnalytics,
  useAddQuestion,
  useVoteQuestion,
  useFavoriteQuestion,
  useMarkLessonComplete,
  useUpdateProgress,
} from '../../hooks/useLessonData';

// Utils
import { formatDuration } from '../../utils/videoUtils';

const LessonDetail = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const toast = useToast();

  // Refs
  const videoRef = useRef(null);
  const markLessonAsCompletedRef = useRef(null);
  const completionNotifiedRef = useRef(false);
  const lessonDataRef = useRef(null);
  const lastSavedProgressRef = useRef(0);

  // State
  const [resumeTime, setResumeTime] = useState(0);
  const [completionNotified, setCompletionNotified] = useState(false);

  // Data Queries
  const { data: lessonResponse, isLoading, error } = useLessonData(lessonId);
  const lessonData = lessonResponse?.data?.lesson;

  const { 
    data: questionsResponse, 
    isLoading: questionsLoading 
  } = useLessonQuestions(lessonId);
  const lessonQuestions = questionsResponse?.data?.questions || [];

  // Fetch linked quiz summary (if any)
  const { data: linkedQuizResponse, isLoading: linkedQuizLoading } = useQuery(
    ['lesson-quiz', lessonId],
    async () => {
      const res = await api.get(`/lessons/${lessonId}/quiz`);
      return res.data;
    },
    {
      enabled: !!lessonId
    }
  );
  const linkedQuiz = linkedQuizResponse?.quiz || null;

  // Debug: log linked quiz fetch result (helps identify 404 / empty responses)
  React.useEffect(() => {
    if (!linkedQuizLoading) {
    }
  }, [linkedQuizLoading, linkedQuizResponse]);

  // جلب إحصائيات الفيديو من Bunny Stream (للمعلمين والإدارة فقط)
  const { data: analyticsData } = useLessonAnalytics(lessonId, user?.role);

  // Mutations
  const addQuestionMutation = useAddQuestion();
  const voteQuestionMutation = useVoteQuestion();
  const favoriteQuestionMutation = useFavoriteQuestion();
  const markLessonCompleteMutation = useMarkLessonComplete();
  const updateProgressMutation = useUpdateProgress();

  // معالج الإكمال (المنطق الأصلي مطور بصرياً)
  const handleLessonCompletion = useCallback(() => {
    if (!completionNotifiedRef.current && lessonId) {
      setCompletionNotified(true);
      completionNotifiedRef.current = true;
      
      toast?.success('🎉 تهانينا! لقد أتممت هذا الدرس بنجاح');
      
      const completionData = {
        progress: 100,
        completed: true,
        lastWatchedPosition: Math.floor(videoRef?.current?.currentTime || 0)
      };
      
      updateProgressMutation.mutate({
        lessonId: parseInt(lessonId),
        progressData: completionData
      });
    }
  }, [lessonId, updateProgressMutation, toast]);

  // Video Controls Hook
  const videoControls = useVideoPlayer(videoRef);

  // تتبع وحفظ التقدم (المنطق الأصلي كاملاً)
  const trackAndSaveProgress = useCallback(
    (currentTime, duration) => {
      if (!lessonId || duration === 0) return;

      const progressPercent = Math.round((currentTime / duration) * 100);
      
      if (Math.abs(progressPercent - lastSavedProgressRef.current) >= 5) {
        lastSavedProgressRef.current = progressPercent;
        
        if (progressPercent >= 90 && !completionNotifiedRef.current) {
          handleLessonCompletion();
        } else {
          const progressData = {
            progress: Math.min(progressPercent, 100),
            lastWatchedPosition: Math.floor(currentTime),
            completed: false
          };
          updateProgressMutation.mutate({
            lessonId: parseInt(lessonId),
            progressData
          });
        }
      }
    },
    [lessonId, updateProgressMutation, handleLessonCompletion]
  );

  useEffect(() => {
    lessonDataRef.current = lessonData;
    markLessonAsCompletedRef.current = markLessonCompleteMutation.mutate;
  }, [lessonData, markLessonCompleteMutation.mutate]);

  // نظام حماية المحتوى (المنطق الأصلي كاملاً وبدون اختصار)
  useEffect(() => {
    const handleContextMenu = (e) => { e.preventDefault(); return false; };
    const handleKeyDown = (e) => {
      if (e.key === 'PrintScreen') { e.preventDefault(); return false; }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); return false; }
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') { e.preventDefault(); return false; }
    };
    const handleCopy = (e) => { e.preventDefault(); return false; };
    const handleDragStart = (e) => { e.preventDefault(); return false; };

    if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
      navigator.mediaDevices.getDisplayMedia = function(...args) {
        toast?.error('تسجيل الشاشة غير مسموح به لحماية حقوق المحتوى');
        return Promise.reject(new DOMException('screen recording disabled', 'NotAllowedError'));
      };
    }

    document.addEventListener('contextmenu', handleContextMenu, false);
    document.addEventListener('keydown', handleKeyDown, false);
    document.addEventListener('copy', handleCopy, false);
    document.addEventListener('dragstart', handleDragStart, false);

    document.body.style.userSelect = 'none';
    document.body.style.WebkitUserSelect = 'none';
    document.body.style.WebkitTouchCallout = 'none';

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.WebkitUserSelect = '';
      document.body.style.WebkitTouchCallout = '';
    };
  }, [toast]);

  // استئناف المشاهدة
  useEffect(() => {
    if (lessonData?.progress?.lastWatchedPosition > 0) {
      setResumeTime(lessonData.progress.lastWatchedPosition);
    }
  }, [lessonData]);

  // مراقبة نجاح الإكمال
  useEffect(() => {
    if (markLessonCompleteMutation.isSuccess) {
      setCompletionNotified(true);
    }
  }, [markLessonCompleteMutation.isSuccess]);

  const handleVideoEnded = () => handleLessonCompletion();

  // تتبع التقدم أثناء المشاهدة
  useEffect(() => {
    if (!videoRef?.current || completionNotified) return;
    const videoElement = videoRef.current;
    const trackProgress = () => {
      if (videoElement.duration > 0) {
        trackAndSaveProgress(videoElement.currentTime, videoElement.duration);
      }
    };
    const interval = setInterval(trackProgress, 1000);
    return () => clearInterval(interval);
  }, [trackAndSaveProgress, completionNotified]);

  // Handlers
  const handleSubmitQuestion = async (questionText) => {
    if (!lessonId || !questionText.trim()) return;
    return addQuestionMutation.mutateAsync({
      lessonId,
      questionText,
    }, {
      onSuccess: () => toast?.success('تم إرسال سؤالك بنجاح!'),
      onError: () => toast?.error('عذراً، فشل إرسال السؤال'),
    });
  };

  const handleVoteQuestion = ({ questionId, voteType }) => voteQuestionMutation.mutate({ questionId, voteType });
  const handleFavoriteQuestion = (questionId) => favoriteQuestionMutation.mutate(questionId);

  const handleBackToCourse = () => {
    if (lessonData?.course_id) navigate(`/courses/${lessonData.course_id}`);
    else navigate('/courses');
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحضير الدرس...</p>
      </div>
    );
  }

  if (error || !lessonData) {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center animate-in fade-in zoom-in duration-500">
        <div className="bg-white rounded-[2.5rem] shadow-xl p-10 border border-gray-100">
          <Info className="w-16 h-16 text-primary-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-4">{error ? 'خطأ في تحميل الدرس' : 'الدرس غير موجود'}</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">{error?.message || 'عذراً، المحتوى الذي تبحث عنه غير متاح حالياً.'}</p>
          <button onClick={handleBackToCourse} className="bg-primary-600 text-white px-10 py-3 rounded-2xl font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-100">
            العودة لمحتوى الكورس
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* Header & Navigation Area */}
          <div className="mb-8 space-y-4">
            <button
              onClick={handleBackToCourse}
              className="flex items-center gap-2 text-gray-400 hover:text-primary-600 font-bold transition-all group text-sm"
            >
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              العودة لمنهج الكورس
            </button>

            <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-gray-100 relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-2 h-full bg-primary-600 opacity-20 group-hover:opacity-100 transition-opacity"></div>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 relative z-10">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="bg-primary-50 text-primary-600 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border border-primary-100">
                       Now Playing
                    </span>
                    {lessonData.content_type && (
                        <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border border-gray-200 flex items-center gap-1.5">
                           {lessonData.content_type === 'video' ? <MonitorPlay size={12} /> : <FileText size={12} />}
                           {lessonData.content_type}
                        </span>
                    )}
                  </div>
                  <h1 className="text-3xl sm:text-4xl font-black text-gray-900 leading-tight">
                    {lessonData.title}
                  </h1>
                  
                  {/* Detailed Meta Grid */}
                  <div className="flex flex-wrap items-center gap-6 pt-2 text-gray-500">
                    <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
                      <User size={16} className="text-primary-600" />
                      <span className="text-sm font-bold text-gray-700">{lessonData.teacher_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} className="text-primary-600" />
                      <span className="text-sm font-medium">{lessonData.course_title}</span>
                    </div>
                     {/* Lesson average rating */}
                     <div className="flex items-center gap-2">
                        <Star size={16} className="text-amber-400" />
                        <span className="text-sm font-medium">{Number(lessonData.average_rating ?? lessonData.rating ?? lessonData.user_rating ?? 0).toFixed(1)} تقييم</span>
                      </div>
                    {(lessonData.duration_seconds > 0 || analyticsData?.data?.stats?.duration) && (
                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-primary-600" />
                        <span className="text-sm font-medium">
                          {analyticsData?.data?.stats?.duration 
                            ? formatDuration(analyticsData.data.stats.duration)
                            : formatDuration(lessonData.duration_seconds)
                          }
                        </span>
                      </div>
                    )}
                    {/* المشاهدات — فقط لدروس Bunny */}
                    {lessonData?.bunny_video_id && (
                      <div className="flex items-center gap-2">
                        <Eye size={16} className="text-primary-600" />
                        <span className="text-sm font-medium">
                          {analyticsData?.data?.stats?.views ?? (lessonData.views || 0)} مشاهدة
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {lessonData?.progress?.completed && (
                    <div className="bg-emerald-50 text-emerald-700 px-6 py-3 rounded-2xl border border-emerald-100 flex items-center gap-3 animate-in zoom-in duration-700">
                        <div className="bg-white p-1 rounded-lg shadow-sm"><CheckCircle2 size={18} /></div>
                        <span className="text-xs font-black uppercase tracking-widest">مكتمل بنجاح</span>
                    </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Workspace Grid */}
          <div className="grid lg:grid-cols-12 gap-8">
            
            {/* Content Display (8 Columns) */}
            <div className="lg:col-span-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
              
              <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden relative group">
                {/* 1. Video Lesson Type */}
                {lessonData?.content_type === 'video' ? (
                  <div className="relative group">
                    <VideoPlayer
                        ref={videoRef}
                        lesson={lessonData}
                        videoControls={videoControls}
                        onTimeUpdate={videoControls.handleTimeUpdate}
                        onLoadedMetadata={videoControls.handleLoadedMetadata}
                        onEnded={handleVideoEnded}
                        onPlay={() => videoControls.setIsPlaying(true)}
                        onPause={() => videoControls.setIsPlaying(false)}
                        resumeTime={resumeTime}
                    />
                  </div>
                ) : 
                /* 2. PDF Lesson Type */
                lessonData?.content_type === 'pdf' ? (
                  <InlinePDFViewer
                    lessonId={lessonId}
                    title={lessonData?.title}
                  />
                ) : 
                /* 4. File Lesson Type */
                lessonData?.content_type === 'file' ? (
                  <div className="p-12 min-h-[400px] bg-gradient-to-br from-amber-50 via-white to-orange-50 flex flex-col items-center justify-center">
                    <div className="text-center space-y-6">
                      <div className="text-6xl">📁</div>
                      <div className="space-y-2">
                        <h3 className="text-2xl font-black text-gray-900">ملف مرفق</h3>
                        <p className="text-gray-600 font-medium">هذا الدرس يحتوي على ملف مرفق (Word, Excel, PowerPoint, إلخ)</p>
                      </div>
                      <button
                        onClick={() => navigate(`/student/file/${lessonId}`)}
                        className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-lg shadow-amber-100 flex items-center justify-center gap-2 mx-auto"
                      >
                        <Download size={20} />
                        فتح الملف
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Unknown Type */
                  <div className="p-20 text-center bg-gray-50">
                    <Lock size={64} className="text-gray-200 mx-auto mb-6" />
                    <h3 className="text-xl font-black text-gray-900">محتوى غير مدعوم</h3>
                    <p className="text-gray-500 mt-2">يرجى الاتصال بالدعم الفني لتحديث المشغل</p>
                  </div>
                )}
              </div>

              {/* Navigation Buttons - Next/Previous Lesson */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Previous Lesson Button */}
                {lessonData?.navigation?.previous ? (
                  <button
                    onClick={() => navigate(`/lessons/${lessonData.navigation.previous.id}`)}
                    className="group bg-white border-2 border-gray-200 hover:border-primary-600 rounded-2xl p-6 transition-all hover:shadow-lg hover:shadow-primary-100 text-right"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-gray-50 rounded-xl group-hover:bg-primary-50 transition-colors">
                        <ChevronLeft size={20} className="text-gray-400 group-hover:text-primary-600 transition-colors" />
                      </div>
                      <div className="flex-1 text-right">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">الدرس السابق</p>
                        <p className="text-gray-900 font-bold line-clamp-2">{lessonData.navigation.previous.title}</p>
                      </div>
                    </div>
                  </button>
                ) : (
                  <div className="bg-gray-50 border-2 border-gray-100 rounded-2xl p-6 text-right opacity-50 cursor-not-allowed">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-white rounded-xl">
                        <ChevronLeft size={20} className="text-gray-300" />
                      </div>
                      <div className="flex-1 text-right">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">الدرس السابق</p>
                        <p className="text-gray-500 font-bold">لا توجد دروس سابقة</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Next Lesson Button */}
                {lessonData?.navigation?.next ? (
                  <button
                    onClick={() => navigate(`/lessons/${lessonData.navigation.next.id}`)}
                    className="group bg-white border-2 border-gray-200 hover:border-primary-600 rounded-2xl p-6 transition-all hover:shadow-lg hover:shadow-primary-100 text-right"
                  >
                    <div className="flex items-start gap-3 justify-between">
                      <div className="p-2 bg-gray-50 rounded-xl group-hover:bg-primary-50 transition-colors">
                        <ChevronLeft size={20} className="text-gray-400 group-hover:text-primary-600 transition-colors transform rotate-180" />
                      </div>
                      <div className="flex-1 text-right">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">الدرس التالي</p>
                        <p className="text-gray-900 font-bold line-clamp-2">{lessonData.navigation.next.title}</p>
                      </div>
                    </div>
                  </button>
                ) : (
                  <div className="bg-gray-50 border-2 border-gray-100 rounded-2xl p-6 text-right opacity-50 cursor-not-allowed">
                    <div className="flex items-start gap-3 justify-between">
                      <div className="p-2 bg-white rounded-xl">
                        <ChevronLeft size={20} className="text-gray-300 transform rotate-180" />
                      </div>
                      <div className="flex-1 text-right">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">الدرس التالي</p>
                        <p className="text-gray-500 font-bold">لا توجد دروس تالية</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Lesson Description Card */}
              {lessonData.description && (
                <div className="bg-white rounded-[2.2rem] p-10 border border-gray-100 shadow-sm relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary-50 rounded-bl-full -z-0 opacity-40 group-hover:scale-110 transition-transform"></div>
                  <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3 relative z-10">
                    <div className="w-1.5 h-6 bg-primary-600 rounded-full"></div>
                    وصف الدرس
                  </h2>
                  <p className="text-gray-600 leading-relaxed text-lg font-medium relative z-10 whitespace-pre-line">
                    {lessonData.description}
                  </p>
                </div>
              )}

              {/* Linked Quiz Card */}
              {linkedQuizLoading ? (
                <div className="bg-white rounded-[2.2rem] p-6 border border-gray-100 shadow-sm relative overflow-hidden group">
                  <div className="flex items-center justify-center">
                    <LoadingSpinner />
                    <span className="text-gray-500 font-bold mr-3">جاري جلب معلومات الاختبار...</span>
                  </div>
                </div>
              ) : linkedQuiz ? (
                <div className="bg-white rounded-[2.2rem] p-6 border border-gray-100 shadow-sm relative overflow-hidden group">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-black text-gray-900">اختبار مرتبط</h3>
                      <p className="text-sm text-gray-600 mt-1">{linkedQuiz.title}</p>
                      <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
                        <div className="bg-gray-50 px-3 py-1 rounded-xl border border-gray-100">{(linkedQuiz.total_questions || linkedQuiz.total_questions === 0) ? `${linkedQuiz.total_questions} سؤال` : ''}</div>
                        {linkedQuiz.duration && <div className="bg-gray-50 px-3 py-1 rounded-xl border border-gray-100">المدة: {linkedQuiz.duration} دقيقة</div>}
                        {linkedQuiz.passing_score && <div className="bg-gray-50 px-3 py-1 rounded-xl border border-gray-100">النجاح: {linkedQuiz.passing_score}%</div>}
                      </div>
                    </div>
                    <div>
                      <button
                        onClick={() => navigate(`/student/quiz/${linkedQuiz.id}`)}
                        className="bg-primary-600 text-white px-4 py-2 rounded-2xl font-black hover:bg-primary-700 transition-all"
                      >
                        ابدأ الاختبار
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-[2.2rem] p-6 border border-gray-100 shadow-sm relative overflow-hidden group">
                  <p className="text-gray-500 font-bold">لا يوجد اختبار مرتبط بهذا الدرس.</p>
                  <p className="text-xs text-gray-400 mt-2">إذا كنت تتوقع وجود اختبار، تحقق من أن المعلم ربط الاختبار بالدرس.</p>
                </div>
              )}

              {/* Enhanced Question Section */}
              {isAuthenticated && user?.role === 'student' && (
                <div className="bg-white rounded-[2.5rem] p-10 border border-gray-100 shadow-sm transition-all hover:shadow-xl">
                  <div className="flex items-center justify-between mb-10 pb-6 border-b border-gray-50">
                    <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                        <MessageCircle size={28} className="text-primary-600" />
                        الأسئلة والأجوبة
                    </h2>
                    <div className="bg-primary-50 text-primary-600 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest">
                        Interactive Mode
                    </div>
                  </div>
                  <QuestionSection
                    questions={lessonQuestions}
                    isLoading={questionsLoading}
                    onSubmitQuestion={handleSubmitQuestion}
                    onVoteQuestion={handleVoteQuestion}
                    onFavoriteQuestion={handleFavoriteQuestion}
                    isSubmitting={addQuestionMutation.isLoading}
                  />
                </div>
              )}
            </div>

            {/* Sidebar Analytics (4 Columns) */}
            <div className="lg:col-span-4 space-y-8 animate-in fade-in slide-in-from-left duration-700">
              <LessonSidebar
                lesson={lessonData}
                userRole={user?.role}
                analytics={analyticsData}
              />
              
              {/* Motivational Learning Card */}
              <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
                <div className="relative z-10 space-y-6">
                    <div className="bg-primary-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl shadow-primary-900/50">
                        <Sparkles size={24} />
                    </div>
                    <h4 className="text-2xl font-black mb-2 leading-tight tracking-tight">استمر في التقدم!</h4>
                    <p className="text-gray-400 text-sm font-medium leading-relaxed">بإمكانك طرح أي سؤال على المدرس من خلال قسم الأسئلة، وسيقوم بالرد عليك في أقرب وقت ممكن.</p>
                    <div className="flex items-center gap-2 text-primary-400 font-black text-[10px] uppercase tracking-widest pt-4 border-t border-white/5">
                         Learning Protection Active <ShieldCheck size={14} />
                    </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default LessonDetail;